'use client';

/**
 * ObeyaPortfolioCard — full-bleed project portfolio listing.
 *
 * The no-selection paint for mode1's canvas. KPI strip + RAG
 * summary + project card grid. Clicking a project opens a
 * `type:'project'` tab and dispatches `setProjectContext` — the tab
 * is what drives `useViewportSelection` to route the canvas to the
 * project preset (`mode1_overview`) and mount the Insights rail.
 * Symmetric with the explorer's `handleOpenProject`.
 *
 * Mode1-native: no `<PortfolioView />` import from the legacy
 * obeya widget. Same data source (`obeya.list` capability →
 * `useObeyaProjects`); same KPI / RAG / project-card paint;
 * stateless leaf composition.
 *
 * @see klyde_edge/composition/cards/obeya.py — content_key="obeya_portfolio"
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import {
  Loader2,
  FolderKanban,
  FileText,
  DollarSign,
  ChevronRight,
} from 'lucide-react';
import { useObeyaProjects } from '@/lib/api/hooks';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useTabStore } from '@/lib/stores/tab-store';
import { cn } from '@/lib/utils/cn';
import { text, surfaceBase } from '@/lib/design/tokens.x';

const RAG_BORDER: Record<string, string> = {
  red: 'border-l-red-500',
  amber: 'border-l-amber-500',
  green: 'border-l-green-500',
  unknown: 'border-l-muted-foreground/30',
};
const RAG_DOT: Record<string, string> = {
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  green: 'bg-green-500',
  unknown: 'bg-muted-foreground/40',
};

export function ObeyaPortfolioCard() {
  const setProjectContext = useGraphSelectionStore((s) => s.setProjectContext);
  const openTab = useTabStore((s) => s.openTab);
  const listStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const lang = useLanguageStore((s) => s.lang);
  const { data, isLoading, error } = useObeyaProjects(listStrategy ?? '', {
    limit: 50,
    ...(lang ? { lang } : {}),
  });
  const { contentKey } = useCardIdentity();
  // Matches useObeyaProjects' key shape (strategy, rag_filter, limit, offset, lang).
  const queryKey = ['obeya-projects', listStrategy ?? '', undefined, 50, undefined, lang] as const;

  const L = data?.labels ?? {};
  const projects = data?.projects ?? [];
  const summary = data?.summary;

  const kpis = useMemo(() => {
    let rfqs = 0,
      pos = 0,
      value = 0;
    for (const p of projects) {
      rfqs += p.rfq_count;
      pos += p.po_count;
      value += p.project_value ?? 0;
    }
    return { projects: projects.length, rfqs, pos, value };
  }, [projects]);

  // Scene export — register the portfolio card into the capture envelope.
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_portfolio',
    status: (data ? 'loaded' : 'loading') as 'loaded' | 'loading',
    query_key: queryKey,
    text: `${kpis.projects} projects · ${kpis.rfqs} RFQs · ${kpis.pos} POs`,
    json: { projects: kpis.projects, rfqs: kpis.rfqs, pos: kpis.pos, value: kpis.value },
  }), [contentKey, data, queryKey, kpis]));

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || projects.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground" style={{ fontSize: surfaceBase.canvas }}>
        <div className="text-center">
          <FolderKanban className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className={text.body}>{error ? L.load_failed : L.no_projects}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto p-4 space-y-4" style={{ fontSize: surfaceBase.canvas }}>
      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          {
            label: L.projects,
            value: String(kpis.projects),
            Icon: FolderKanban,
            accent: 'text-blue-400',
          },
          {
            label: L.rfqs,
            value: String(kpis.rfqs),
            Icon: FileText,
            accent: 'text-yellow-400',
          },
          {
            label: L.portfolio_value,
            value:
              kpis.value > 0
                ? `$${kpis.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                : '—',
            Icon: DollarSign,
            accent: 'text-emerald-400',
          },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className="bg-card rounded-xl border border-border p-3.5"
          >
            <div className="flex items-center justify-between mb-1.5">
              <span className={text.eyebrow}>
                {kpi.label}
              </span>
              <kpi.Icon className={cn('w-3.5 h-3.5', kpi.accent)} />
            </div>
            <p className={text.value}>
              {kpi.value}
            </p>
          </div>
        ))}
      </div>

      {/* RAG summary */}
      {summary && (
        <div className="flex items-center gap-3 text-xs">
          {[
            { label: L.red, count: summary.red, color: 'text-red-500', dot: 'bg-red-500' },
            { label: L.amber, count: summary.amber, color: 'text-amber-500', dot: 'bg-amber-500' },
            { label: L.green, count: summary.green, color: 'text-green-500', dot: 'bg-green-500' },
          ].map((r) => (
            <div key={r.label} className="flex items-center gap-1.5">
              <span className={cn('w-2 h-2 rounded-full', r.dot)} />
              <span className={cn('font-medium', r.color)}>{r.count}</span>
              <span className="text-muted-foreground">{r.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Project cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {projects.map((project) => (
          <button
            key={project.semantic_id}
            onClick={() => {
              // Order matters: openTab() calls clearSelection() internally
              // (tab-store.ts:59), which nulls graph-store.projectId. So
              // openTab MUST run before setProjectContext — otherwise the
              // clear wipes the projectId the explorer's expand-on-match
              // effect needs. Same id scheme as the explorer's
              // handleOpenProject so both paths share one tab; the tab is
              // what routes the viewport to the project preset + rail.
              openTab({
                id: `project-${project.semantic_id}`,
                type: 'project',
                title: project.name,
                entityId: project.semantic_id,
              });
              setProjectContext(project.semantic_id, {
                name: project.name,
                rag_status: project.rag_status,
                rag_label: project.rag_label,
                rfq_count: project.rfq_count,
                po_count: project.po_count,
              });
            }}
            className={cn(
              'group text-left bg-card rounded-xl border border-border border-l-2 p-4',
              RAG_BORDER[project.rag_status] ?? RAG_BORDER.unknown,
              'hover:bg-accent/50 hover:border-foreground/20 hover:shadow-md',
              'hover:-translate-y-0.5 transition-all cursor-pointer',
            )}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className={cn(
                    'w-2 h-2 rounded-full shrink-0',
                    RAG_DOT[project.rag_status] ?? RAG_DOT.unknown,
                  )}
                />
                <span className={cn(text.title, 'truncate')}>
                  {project.name}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
            </div>
            <div className="flex items-center gap-2 mb-3">
              {project.customer_name && (
                <span className={cn(text.muted, 'truncate')}>
                  {project.customer_name}
                </span>
              )}
              {project.rag_label && (
                <span className={cn(text.muted, 'truncate ml-auto')}>
                  {project.rag_label}
                </span>
              )}
            </div>
            {project.project_value != null && project.project_value > 0 && (
              <div className={cn('flex items-center gap-1 mb-2 text-emerald-400', text.title)}>
                <DollarSign className="w-3.5 h-3.5" />
                <span>
                  {project.project_value.toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  })}
                </span>
              </div>
            )}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <FileText className="w-3 h-3" />
                <span>
                  {project.rfq_count} RFQ{project.rfq_count !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
